import * as d3 from "d3";
import { useEffect, useRef, useState, useContext } from "react";
import { Modal, Steps, Button, Collapse, Timeline, Tag, Card, Row, Col } from "antd";
import {
  EnvironmentOutlined,
  ClockCircleFilled,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  FileTextOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import SampleImg from "../../assests/sample.jpg";

const { Step } = Steps;
const { Panel } = Collapse;

export default function CircularOverAll({ onBack }) {
  const ref = useRef();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month());
  const { authData, backUrl } = useContext(AuthContext);
  const [overAllMonthData, setOverAllMonthData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [loading, setLoading] = useState(false);

  const [showOnDuty, setShowOnDuty] = useState(false);
  const [onDutyRequests, setOnDutyRequests] = useState([]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/hr/attendance-comparsion/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
        params: { month: month + 1, year },
      });
      setOverAllMonthData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setOverAllMonthData([]);
    }
  };

  const fetchOnduty = async (date) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backUrl}/api/hr/attendance/my-onduty-requests/?from_date=${date}&to_date=${date}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setOnDutyRequests(response.data);
    } catch (err) {
      console.log(err.response?.data?.message || err.message);
      console.log("Failed to fetch onduty requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const filteredData = overAllMonthData.filter(
    (item) => item.Date === selectedDate
  );

  // Filter to only show records with FirstInTime
  const validFilteredData = filteredData.filter(
    (item) => item?.FirstInTime !== null && item?.FirstInTime !== undefined
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "#16a34a";
      case "PENDING":
        return "#d97706";
      case "REJECTED":
        return "#dc2626";
      default:
        return "#888";
    }
  };

  const getAttendanceStatusColor = (status, remarks) => {
    const statusColorMap = {
      PRESENT: "#00AF50",                        // dark green
      ABSENT: "#FE0000",                         // red
      "GRACE - LATE IN": "#FFFECB",             // light mint green
      "GRACE – EARLY OUT": "#FFFECB",           // light mint green
      "LATE IN": "#FFFECB",                      // light yellow
      "EARLY OUT": "#FFFECB",                    // light yellow
      "SHORTFALL PRESENT": "#298988",            // dark teal
      "HALF DAY - SHORTFALL PRESENT": "#58BABB", // medium teal/cyan
      "SHORT HOURS": "#DED9C5",                  // beige/tan
      "MISPUNCH OUT": "#BFBFBF",                 // medium gray
      "MISPUNCH IN": "#BFBFBF",                  // medium gray
      PERMISSION: "#98FF66",                     // bright lime green
      HOLIDAY: "#F79647",                        // orange
      SUNDAY: "#FFFF00",                         // pure yellow
      COMPOFF: "#1FA2CC",                        // sky/cyan blue
      DEFAULT: "white",
      WEEKOFF: "#FFFF00",                        // pure yellow
      TECHNICAL_ISSUE: "#303343",
      CASUAL: "#CB00CC",                         // magenta/purple
      SICK: "#FF0066",                           // hot pink / deep pink
      "HALF DAY - CASUAL LEAVE": "#FE66CB",             // light pink
      "HALF DAY - SICK LEAVE": "#B1A1C8",
      "HALF DAY - CASUAL": "#FE66CB",
      "HALF DAY - SICK": "#B1A1C8",
      "LOP": "#bf4406",
      "HALF DAY - COMPOFF": "#7c9150",
      "HALF DAY - WEEKOFF": "#a068a3",
    };
    return (
      statusColorMap[remarks?.toUpperCase()] ||
      statusColorMap[status?.toUpperCase()] ||
      "#d1d5db"
    );
  };

  const getDayColor = (dateStr) => {
    const day = dayjs(dateStr).startOf("day");
    const today = dayjs().startOf("day");

    // If it's a future date, return white with no records
    if (day.isAfter(today)) {
      return "#ffffff";
    }

    const dayRecords = overAllMonthData.filter((item) => item.Date === dateStr);
    if (!dayRecords.length) {
      if (day.day() === 0) return "#FFFF00"; // Sunday is always yellow
      return "#d1d5db";
    }
    let color = day.day() === 0 ? "#FFFF00" : "#d1d5db";
    for (const rec of dayRecords) {
      const c = getAttendanceStatusColor(rec.Status, rec.AttendanceStatus);
      if (c && c !== "white" && c !== "#ffffff") {
        color = c;
        break;
      }
    }
    return color;
  };

  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const width = ref.current.clientWidth;
    const isMobile = width < 768;
    const height = isMobile ? 320 : 420;
    const cellSize = isMobile ? 40 : 70;
    const radius = isMobile ? 15 : 25;

    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);
    const startDate = d3.timeSunday.floor(startOfMonth);
    const endDate = d3.timeSaturday.ceil(endOfMonth);
    const days = d3.timeDays(startDate, d3.timeDay.offset(endDate, 1));

    const numWeeks = Math.ceil(days.length / 7);
    const totalWidth = 7 * cellSize;
    const totalHeight = numWeeks * cellSize;

    const offsetX = (width - totalWidth) / 2 + cellSize / 2;
    const offsetY = (height - totalHeight) / 2 + 30;

    const g = svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .append("g")
      .attr("transform", `translate(${offsetX}, ${offsetY})`);

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    g.selectAll("text-weekday")
      .data(daysOfWeek)
      .enter()
      .append("text")
      .attr("x", (d, i) => i * cellSize)
      .attr("y", -30)
      .attr("text-anchor", "middle")
      .attr("font-size", width < 500 ? "11px" : "14px")
      .attr("font-weight", "600")
      .attr("fill", "#0e497b")
      .text((d) => d);

    g.selectAll("circle-day")
      .data(days)
      .enter()
      .append("circle")
      .attr("cx", (d) => d.getDay() * cellSize)
      .attr("cy", (d, i) => Math.floor(i / 7) * cellSize)
      .attr("r", radius)
      .attr("fill", (d) => getDayColor(d3.timeFormat("%Y-%m-%d")(d)))
      .attr("stroke", (d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        const bgColor = getDayColor(dateStr);
        return bgColor === "#ffffff" ? "#e5e7eb" : "#fff";
      })
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        setSelectedDate(dateStr);
        setIsModalOpen(true);
        fetchOnduty(dateStr);
      })
      .on("mouseover", function () {
        d3.select(this).transition().duration(120).attr("r", radius + 4);
      })
      .on("mouseout", function () {
        d3.select(this).transition().duration(120).attr("r", radius);
      });

    g.selectAll("text-day")
      .data(days)
      .enter()
      .append("text")
      .attr("x", (d) => d.getDay() * cellSize)
      .attr("y", (d, i) => Math.floor(i / 7) * cellSize + 5)
      .attr("text-anchor", "middle")
      .attr("font-size", width < 500 ? "10px" : "12px")
      .attr("font-weight", "bold")
      .attr("fill", (d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        const bgColor = getDayColor(dateStr);
        if (d.getMonth() !== month) return "#9ca3af";
        // If background is white or light yellow, use dark text.
        if (
          bgColor?.toUpperCase() === "#FFFFFF" ||
          bgColor?.toUpperCase() === "#FFFECB" ||
          bgColor?.toUpperCase() === "#FFFF00"
        )
          return "#333";
        return "#fff";
      })
      .text((d) => d.getDate());
  }, [month, year, overAllMonthData]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const renderOnDutyRequests = () => {
    if (!showOnDuty || !onDutyRequests || onDutyRequests.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: 30, padding: isMobile ? "0 10px" : "0 40px" }}>
        <h3
          style={{
            fontSize: isMobile ? 18 : 20,
            fontWeight: 700,
            marginBottom: 20,
            color: "#0e497b",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FileTextOutlined />
          On-Duty Requests
        </h3>

        {onDutyRequests.map((request) => (
          <Card
            key={request.id}
            style={{
              marginBottom: 20,
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `2px solid ${getStatusColor(request.status)}`,
            }}
            bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                  {request.employee_name} ({request.employee_code})
                </div>
                <div style={{ marginBottom: 6 }}>
                  <p
                    style={{
                      fontSize: isMobile ? 12 : 13,
                      color:
                        request.status === "APPROVED" ? "success"
                          : request.status === "PENDING" ? "warning"
                            : "error",
                    }}
                  >
                    {request.status}
                  </p>
                </div>
                <p><strong>Onduty Type:</strong> {request.onduty_type.toUpperCase()}</p>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
                marginBottom: 16,
                backgroundColor: "#ffffff",
                padding: isMobile ? "12px" : "16px",
                borderRadius: 8,
                border: "1px solid #ffffff",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />Start Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.start_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />End Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.end_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />Location
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {request.location}
                </div>
              </div>
              {request.remarks && (
                <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Remarks</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#374151", fontStyle: "italic" }}>
                    "{request.remarks}"
                  </div>
                </div>
              )}
            </div>

            <Collapse bordered={false} style={{ backgroundColor: "#fff", borderRadius: 8 }}>
              <Panel
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>
                    Approval Logs ({request.approval_logs?.filter((log) => log.status === "APPROVED").length || 0}/{request.approval_logs?.length || 0})
                  </div>
                }
                key="1"
              >
                <Timeline
                  style={{ marginTop: 16 }}
                  items={request.approval_logs?.map((log) => ({
                    color: getStatusColor(log.status),
                    children: (
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: "#333" }}>{log.approver_name}</div>
                            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                              Updated: {dayjs(log.updated_at).format("MMM DD, YYYY hh:mm A")}
                            </div>
                          </div>
                          <Tag color={log.status === "APPROVED" ? "success" : log.status === "PENDING" ? "warning" : "error"} style={{ fontSize: 12 }}>
                            {log.status}
                          </Tag>
                        </div>
                        <div style={{ backgroundColor: "#f9fafb", padding: "8px 12px", borderRadius: 6, fontSize: 13, color: "#555", fontStyle: "italic", marginTop: 8 }}>
                          <strong>Remarks: </strong>{log.remarks ? log.remarks : "No remarks found"}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </Panel>
            </Collapse>
          </Card>
        ))}
      </div>
    );
  };

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear((prev) => prev - 1); }
    else setMonth((prev) => prev - 1);
  };

  const goNext = () => {
    if (month === 11) { setMonth(0); setYear((prev) => prev + 1); }
    else setMonth((prev) => prev + 1);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, marginTop: 10 }}>
        <Button
          onClick={onBack}
          icon={<CalendarOutlined />}
          style={{ backgroundColor: "#0e497b", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600 }}
        >
          Show Today's Attendance
        </Button>
      </div>

      <div
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          width: "100%", minHeight: "90vh", background: "#f8fafc", padding: "20px 10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <Button icon={<LeftOutlined />} onClick={goPrev} />
          <strong style={{ fontSize: "clamp(16px, 2vw, 20px)", margin: "0 10px", color: "#0e497b" }}>
            {monthNames[month]} {year}
          </strong>
          <Button icon={<RightOutlined />} onClick={goNext} />
        </div>

        <svg ref={ref} style={{ width: "95%", maxWidth: 700, height: 420 }}></svg>
      </div>

      {/* --- EXECUTIVE CALENDAR DAY RECORD DETAILS MODAL --- */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={window.innerWidth < 700 ? "95%" : 850}
        centered
        closeIcon={<span style={{ color: "#fff", fontSize: 18, fontWeight: 900 }}>✕</span>}
        modalRender={modal => (
          <div style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 24px 48px rgba(15,23,42,0.22)" }}>
            {modal}
          </div>
        )}
        title={null}
        bodyStyle={{ padding: 0 }}
      >
        {/* EXECUTIVE HEADER BANNER */}
        <div
          style={{
            background: "linear-gradient(135deg, #0e497b 0%, #1e293b 100%)",
            padding: "24px 28px",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12
          }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px", color: "#93c5fd", marginBottom: 4 }}>
              📅 ATTENDANCE AUDIT SHEET
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#ffffff" }}>
              {selectedDate ? dayjs(selectedDate).format("dddd, MMMM D, YYYY") : "Attendance Record"}
            </div>
          </div>

          {validFilteredData.length > 0 && (() => {
            const firstItem = validFilteredData[0];
            const statusColor = getAttendanceStatusColor(firstItem.Status, firstItem.AttendanceStatus);
            const statusLabel = firstItem.Status || firstItem.AttendanceStatus || "RECORDED";
            return (
              <span
                style={{
                  background: `${statusColor}30`,
                  border: `1.5px solid ${statusColor}`,
                  color: "#ffffff",
                  padding: "6px 16px",
                  borderRadius: 20,
                  fontWeight: 900,
                  fontSize: 13,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor }} />
                {statusLabel.toUpperCase()}
              </span>
            );
          })()}
        </div>

        {/* MODAL BODY CONTENT */}
        <div style={{ padding: "24px 28px", background: "#f8fafc", maxHeight: "75vh", overflowY: "auto" }}>
          {validFilteredData.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 20px" }}>
              <CalendarOutlined style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 800, color: "#334155" }}>No Punches Recorded</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>There are no biometric or mobile check-in logs for this date.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {validFilteredData.map((item, index) => {
                const punchDetails = item?.punch_details || [];
                const statusColor = getAttendanceStatusColor(item.Status, item.AttendanceStatus);

                return (
                  <div
                    key={index}
                    style={{
                      background: "#ffffff",
                      borderRadius: 18,
                      border: "1px solid #e2e8f0",
                      padding: 20,
                      boxShadow: "0 4px 14px rgba(0,0,0,0.03)"
                    }}
                  >
                    {/* TOP STATUS HEADER INSIDE ITEM CARD */}
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2" style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <div className="d-flex align-items-center gap-2">
                        <ClockCircleFilled style={{ color: statusColor, fontSize: 18 }} />
                        <span style={{ fontWeight: 900, color: "#0f172a", fontSize: 15 }}>
                          Shift & Log Session #{index + 1}
                        </span>
                      </div>
                      <Tag color={statusColor} style={{ borderRadius: 10, fontWeight: 800, fontSize: 12, padding: "2px 10px" }}>
                        {item.AttendanceStatus || item.Status || "Active Log"}
                      </Tag>
                    </div>

                    {/* PUNCH DETAILS GRID */}
                    {punchDetails.map((punch, punchIdx) => {
                      const checkInTime = punch.CheckIn || punch.PunchIn;
                      const checkOutTime = punch.CheckOut || punch.PunchOut;

                      return (
                        <div key={punchIdx} className="mb-2">
                          {/* WORK SITES BANNER */}
                          {punch?.RecordType !== "BIOMETRIC" && (punch.check_in_work_site || punch.check_out_work_site) && (
                            <div className="mb-3 p-2.5 d-flex align-items-center gap-2" style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, fontSize: 12.5, fontWeight: 800, color: "#0369a1" }}>
                              <EnvironmentOutlined style={{ fontSize: 15, color: "#0284c7" }} />
                              <span>Work Site: {punch.check_in_work_site && `[IN: ${punch.check_in_work_site}]`} {punch.check_out_work_site && `[OUT: ${punch.check_out_work_site}]`}</span>
                            </div>
                          )}

                          {/* PUNCH IN / OUT TIME METRIC CARDS */}
                          <Row gutter={[16, 16]}>
                            {/* PUNCH IN CARD */}
                            <Col xs={24} sm={12}>
                              <div
                                style={{
                                  background: "linear-gradient(145deg, #f0fdf4 0%, #dcfce7 100%)",
                                  border: "1.5px solid #86efac",
                                  borderRadius: 16,
                                  padding: "16px 18px",
                                  boxShadow: "0 4px 12px rgba(16,185,129,0.06)"
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <span style={{ fontSize: 11, fontWeight: 900, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                                    🟢 PUNCH IN RECORD
                                  </span>
                                  <Tag color="green" style={{ borderRadius: 10, fontWeight: 800, fontSize: 10.5, margin: 0 }}>
                                    {checkInTime ? "VERIFIED" : "NO LOG"}
                                  </Tag>
                                </div>

                                <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a", marginTop: 6, letterSpacing: "-0.5px" }}>
                                  {checkInTime || "Not Recorded"}
                                </div>

                                {punch.check_in_photo_url && (
                                  <div className="mt-3 pt-2" style={{ borderTop: "1px dashed #a7f3d0" }}>
                                    <div style={{ fontSize: 10.5, fontWeight: 800, color: "#166534", marginBottom: 6 }}>Check-In Selfie Verification</div>
                                    <img
                                      src={punch.check_in_photo_url}
                                      alt="Check-In Selfie"
                                      onClick={() => window.open(punch.check_in_photo_url, "_blank")}
                                      style={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 14,
                                        objectFit: "cover",
                                        border: "2.5px solid #4ade80",
                                        cursor: "pointer",
                                        boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                                        transition: "transform 0.2s ease"
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>

                            {/* PUNCH OUT CARD */}
                            <Col xs={24} sm={12}>
                              <div
                                style={{
                                  background: "linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%)",
                                  border: "1.5px solid #93c5fd",
                                  borderRadius: 16,
                                  padding: "16px 18px",
                                  boxShadow: "0 4px 12px rgba(59,130,246,0.06)"
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <span style={{ fontSize: 11, fontWeight: 900, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                                    🔴 PUNCH OUT RECORD
                                  </span>
                                  <Tag color={checkOutTime ? "blue" : "orange"} style={{ borderRadius: 10, fontWeight: 800, fontSize: 10.5, margin: 0 }}>
                                    {checkOutTime ? "VERIFIED" : "NO PUNCHOUT"}
                                  </Tag>
                                </div>

                                <div style={{ fontSize: checkOutTime ? 22 : 16, fontWeight: 900, color: checkOutTime ? "#0f172a" : "#c2410c", marginTop: 6, letterSpacing: "-0.5px" }}>
                                  {checkOutTime || "NO PUNCHOUT"}
                                </div>

                                {punch.check_out_photo_url && (
                                  <div className="mt-3 pt-2" style={{ borderTop: "1px dashed #bfdbfe" }}>
                                    <div style={{ fontSize: 10.5, fontWeight: 800, color: "#1e40af", marginBottom: 6 }}>Check-Out Selfie Verification</div>
                                    <img
                                      src={punch.check_out_photo_url}
                                      alt="Check-Out Selfie"
                                      onClick={() => window.open(punch.check_out_photo_url, "_blank")}
                                      style={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 14,
                                        objectFit: "cover",
                                        border: "2.5px solid #60a5fa",
                                        cursor: "pointer",
                                        boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                                        transition: "transform 0.2s ease"
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* ON DUTY REQUESTS SECTION */}
          {renderOnDutyRequests()}
        </div>
      </Modal>
    </>
  );
}